// Iter 116 P1 — Spec Section detail page.
// Renders the section's full content + every cross-reference into and
// out of the section. Reads only existing endpoints:
//   GET /api/spec-sections/{id}        — full doc (text + meta + refs[])
//   GET /api/references/spec_section/{id} — refs already on this section
//   GET /api/sheets/{id}                — to resolve sheet labels (one call per ref)
// No new backend.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Layers, Lightbulb, ExternalLink, Inbox, Loader2,
  Flame,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { safeExternalHref } from "@/lib/utils";

const TYPE_ICON = {
  sheet: FileText,
  spec_section: Layers,
  finding: Lightbulb,
  intake_document: Inbox,
  core_reference: ExternalLink,
};
const TYPE_LABEL = {
  sheet: "Sheet",
  spec_section: "Spec section",
  finding: "Field finding",
  intake_document: "Intake document",
  core_reference: "CORE record",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
}

export default function SpecSectionDetail() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [sourceUrl, setSourceUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!sectionId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/spec-sections/${sectionId}`)
      .then((r) => {
        if (cancelled) return;
        setDoc(r.data); setErr(null);
        // Iter 118 P0.5 — fetch the source intake doc to expose the
        // "Open original spec book" deep link with #page={page_start}.
        const sid = r.data?.source_document_id;
        if (sid) {
          api.get(`/intake-documents/${sid}`).then((rr) => {
            if (cancelled) return;
            const u = rr.data?.external_url || rr.data?.source_url || null;
            // Security hardening (2026-09-08, SEC-001 follow-up) —
            // `external_url`/`source_url` aren't currently persisted by
            // any write path (defensive-only today), but sanitize at
            // the point of use anyway so a future writer can never
            // smuggle a javascript: URI into this <a href>.
            setSourceUrl(safeExternalHref(u));
          }).catch(() => { if (!cancelled) setSourceUrl(null); });
        }
      })
      .catch((e) => { if (!cancelled) { setErr(e?.response?.data?.detail || "Not found"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sectionId]);

  const refs = useMemo(() => doc?.references || [], [doc]);
  const refsByType = useMemo(() => {
    const out = { sheet: [], finding: [], intake_document: [], core_reference: [] };
    for (const r of refs) {
      const t = r.target_type;
      if (out[t]) out[t].push(r);
    }
    return out;
  }, [refs]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[var(--fop-text-3)]" data-testid="spec-section-loading">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading spec section…
      </div>
    );
  }
  if (err || !doc) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="spec-section-error">
        <div className="font-display text-lg text-[var(--fop-text)] mb-2">Spec section not found</div>
        <div className="text-sm text-[var(--fop-text-3)]">{err}</div>
        <button onClick={() => navigate(-1)} className="btn btn-ghost text-xs mt-4 inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-6" data-testid="spec-section-detail">
      <button onClick={() => navigate(-1)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-[var(--fop-text-3)]" data-testid="spec-section-back">
        <ArrowLeft size={11} /> Back
      </button>

      {/* Header — exactly what the brief asked for */}
      <header className="fop-surface-2 border border-[var(--fop-border-hi)] rounded-lg p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Spec section
          </div>
          {/* Iter 118 P1 — derived Hotspot indicator. NO new storage. */}
          {refs.length >= 8 && (
            <span
              data-testid="spec-hotspot-badge"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-orange-500/20 text-orange-300 border border-orange-400/40"
              title={`Linked to ${refs.length} other knowledge objects`}
            >
              <Flame size={10} /> High activity · {refs.length} links
            </span>
          )}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl text-[var(--fop-text)] mt-1" data-testid="spec-section-title">
          <span className="font-mono text-[var(--fop-primary)] mr-2">{doc.section_number}</span>
          {doc.section_title || "—"}
        </h1>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <Meta label="Division" value={doc.division_number ? `${doc.division_number}${doc.division_title ? " · " + doc.division_title : ""}` : "—"} testid="spec-section-division" />
          <Meta label="Source" value={
            doc.source_document_id
              ? <Link className="text-[var(--fop-primary)] hover:underline" to={`/intake?focus=${doc.source_document_id}`} data-testid="spec-section-source">Document</Link>
              : "—"
          } />
          <Meta label="Pages" value={doc.page_start ? `${doc.page_start}${doc.page_end && doc.page_end !== doc.page_start ? "–" + doc.page_end : ""}` : "—"} />
          <Meta label="Updated" value={fmtDate(doc.updated_at || doc.created_at)} />
        </dl>
        {/* Iter 118 P0.5 — Open Original Specification deep link.
            Uses the intake doc's external_url + a #page= fragment so the
            PDF viewer jumps straight to the section's first page. */}
        {sourceUrl && (
          <div className="mt-4 pt-4 border-t border-[var(--fop-border)]">
            <a
              href={`${sourceUrl}${doc.page_start ? "#page=" + doc.page_start : ""}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-source-pdf"
              className="btn btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2"
            >
              <ExternalLink size={12} /> Open original spec book{doc.page_start ? ` · page ${doc.page_start}` : ""}
            </a>
          </div>
        )}
      </header>

      {/* Body — section text */}
      <section className="fop-surface-2 border border-[var(--fop-border)] rounded-lg p-5" data-testid="spec-section-body">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">Section text</div>
        {doc.text ? (
          <pre className="whitespace-pre-wrap text-sm text-[var(--fop-text)] leading-relaxed font-sans">{doc.text}</pre>
        ) : (
          <div className="text-sm text-[var(--fop-text-3)]">No extracted text on file.</div>
        )}
      </section>

      {/* Traceability — every cross-reference grouped by target type.
          Each panel header includes the count so users see coverage
          before clicking. */}
      <RefsPanel title="Referenced sheets"        items={refsByType.sheet}            type="sheet"            testid="spec-refs-sheets" />
      <RefsPanel title="Referenced findings"      items={refsByType.finding}          type="finding"          testid="spec-refs-findings" />
      <RefsPanel title="Referenced intake docs"   items={refsByType.intake_document}  type="intake_document"  testid="spec-refs-intake" />
      <RefsPanel title="Referenced CORE records"  items={refsByType.core_reference}   type="core_reference"   testid="spec-refs-core" />

      {/* Iter 117 Phase 4 — at-a-glance coverage row, also surfaces zero
          counts so users can SEE that this section is e.g. linked to 17
          sheets but no findings yet. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="spec-coverage-summary">
        <CoverageChip label="Sheets" count={refsByType.sheet.length} testid="cov-sheets" />
        <CoverageChip label="Findings" count={refsByType.finding.length} testid="cov-findings" />
        <CoverageChip label="Intake docs" count={refsByType.intake_document.length} testid="cov-intake" />
        <CoverageChip label="CORE records" count={refsByType.core_reference.length} testid="cov-core" />
      </div>
    </div>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <dt className="text-[10px]">{label}</dt>
      <dd className="text-sm font-sans text-[var(--fop-text)] mt-0.5 normal-case tracking-normal">{value}</dd>
    </div>
  );
}

// Iter 117 Phase 4 — coverage chip surfaces the count of cross-references
// per target_type so a user can verify spec coverage without opening
// each panel.
function CoverageChip({ label, count, testid }) {
  const empty = !count;
  return (
    <div
      className={`fop-surface border ${empty ? "border-[var(--fop-border)] text-[var(--fop-text-3)]" : "border-[var(--fop-primary)]/40 text-[var(--fop-text)]"} rounded-lg p-3 text-center`}
      data-testid={testid}
    >
      <div className="text-2xl font-semibold leading-none">{count}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mt-1 opacity-80">{label}</div>
    </div>
  );
}

function RefsPanel({ title, items, type, testid }) {
  if (!items || items.length === 0) {
    return (
      <section className="fop-surface border border-[var(--fop-border)] rounded-lg p-4" data-testid={testid}>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2">{title}</div>
        <div className="text-[11px] font-mono text-[var(--fop-text-4)]">No links yet.</div>
      </section>
    );
  }
  const Icon = TYPE_ICON[type] || FileText;
  // Iter 116 P2 polish — CORE refs link out to the external URL when present.
  // Security hardening (2026-09-08, SEC-001): the backend now rejects
  // non-http(s) schemes at write time (`_CoreRefCreate.url` validator),
  // but sanitize again here so this render is safe even against any
  // row written before that fix.
  const externalHref = (r) => (type === "core_reference" && r.url) ? safeExternalHref(r.url) : null;
  return (
    <section className="fop-surface border border-[var(--fop-border)] rounded-lg p-4" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">{title} · {items.length}</div>
      <ul className="space-y-1">
        {items.map((r, i) => {
          const href = destinationFor(r);
          const ext = externalHref(r);
          if (ext) {
            return (
              <li key={i} data-testid={`${testid}-row-${i}`}>
                <a href={ext} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--fop-surface-2)] text-sm text-[var(--fop-text)]">
                  <Icon size={12} className="text-[var(--fop-primary)] shrink-0" />
                  <span className="truncate">{r.label || r.target_id}</span>
                  <ExternalLink size={11} className="text-[var(--fop-text-3)] ml-auto" />
                </a>
              </li>
            );
          }
          return (
            <li key={i} data-testid={`${testid}-row-${i}`}>
              {href ? (
                <Link to={href} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--fop-surface-2)] text-sm text-[var(--fop-text)]">
                  <Icon size={12} className="text-[var(--fop-primary)] shrink-0" />
                  <span className="truncate">{r.label || r.target_id}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-4)]">{TYPE_LABEL[type] || type}</span>
                </Link>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-[var(--fop-text-3)]">
                  <Icon size={12} className="shrink-0" />
                  <span className="truncate">{r.label || r.target_id}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function destinationFor(ref) {
  switch (ref.target_type) {
    case "sheet": return `/sheet/${ref.target_id}`;
    case "spec_section": return `/spec-section/${ref.target_id}`;
    case "finding": return `/discoveries?focus=${ref.target_id}`;
    case "intake_document": return `/intake?focus=${ref.target_id}`;
    case "core_reference": return null; // external — handled via URL on the row when present
    default: return null;
  }
}

// Reusable RefsPanel for other surfaces (sheets, findings).
SpecSectionDetail.RefsPanel = RefsPanel;
SpecSectionDetail.destinationFor = destinationFor;
